'use client';

import { c as _c } from "react/compiler-runtime";
import { addresses, type ChainId, type MarketId } from '@morpho-org/blue-sdk';
import { blueAbi } from '@morpho-org/blue-sdk-viem';
import { ZERO } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon } from '@repo/ui';
import { Alert, Close } from '@repo/ui/icons';
import { memo, useMemo } from 'react';
import { encodeFunctionData } from 'viem';
import { useWalletClient } from 'wagmi';
import type { TransactionBuilder } from '@/contexts/Transaction/TransactionV2.context';
import { useAutoDeleverage } from '@/components/common/AutoDeleverage/hooks/useAutoDeleverage';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { logger } from '@/lib/observability/logger';
import AutoDeleverageRecap from './AutoDeleverageRecap';
export interface AutoDeleverageReviewDialogProps {
  toEnable: boolean;
  marketId: MarketId;
  chainId: ChainId;
}
const AutoDeleverageReviewDialog = t0 => {
  const $ = _c(45);
  const {
    toEnable,
    marketId,
    chainId
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    account,
    targetChain,
    switchToTargetChain
  } = useWeb3Context();
  const {
    data: client
  } = useWalletClient();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    preLiquidationContractAddress,
    isPreLiquidation,
    refetchAuthorizationQuery,
    status,
    refetchAll
  } = useAutoDeleverage(marketId, chainId);
  const isEnableProbePending = toEnable && status === "loading";
  const isEnableProbeUnavailable = toEnable && status !== "loading" && isPreLiquidation === undefined;
  let t1;
  bb0: {
    if (!preLiquidationContractAddress) {
      t1 = undefined;
      break bb0;
    }
    if (chainId !== targetChain.id) {
      t1 = undefined;
      break bb0;
    }
    if (toEnable && isPreLiquidation !== true) {
      t1 = undefined;
      break bb0;
    }
    const to = addresses[chainId].blue;
    let t2;
    if ($[0] !== preLiquidationContractAddress || $[1] !== toEnable) {
      t2 = encodeFunctionData({
        abi: blueAbi,
        functionName: "setAuthorization",
        args: [preLiquidationContractAddress, toEnable]
      });
      $[0] = preLiquidationContractAddress;
      $[1] = toEnable;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const data = t2;
    let t3;
    if ($[3] !== data || $[4] !== preLiquidationContractAddress || $[5] !== to || $[6] !== toEnable) {
      t3 = {
        buildTx: () => ({
          to,
          value: ZERO,
          data,
          action: {
            type: "blueAuthorization",
            args: {
              authorized: preLiquidationContractAddress,
              isAuthorized: toEnable
            }
          }
        })
      };
      $[3] = data;
      $[4] = preLiquidationContractAddress;
      $[5] = to;
      $[6] = toEnable;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t1 = t3;
  }
  const transaction = t1;
  let t2;
  if ($[8] !== marketId || $[9] !== toEnable) {
    t2 = <AutoDeleverageRecap toEnable={toEnable} marketId={marketId} />;
    $[8] = marketId;
    $[9] = toEnable;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const recap = t2;
  let t3;
  if ($[11] !== account || $[12] !== client || $[13] !== recap || $[14] !== refetchAuthorizationQuery || $[15] !== submitTransactions || $[16] !== targetChain || $[17] !== toEnable || $[18] !== toggleDialog || $[19] !== transaction) {
    t3 = () => {
      if (!transaction || !client || !account.isWalletTypeResolved) {
        return;
      }
      submitTransactions(client, {
        actionType: "toggleAutoDeleverage",
        flow: "autoDeleverage",
        transaction,
        requirements: [],
        onTxSuccess: refetchAuthorizationQuery,
        chain: targetChain,
        account
      }).catch(error => {
        logger.error(error instanceof Error ? error : new Error(String(error)), {
          tags: {
            component: "AutoDeleverageReviewDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage={`${toEnable ? "Authorize" : "Disable"} Auto-Deleverage contract`} operationContent={recap} />, {
        closable: false
      });
    };
    $[11] = account;
    $[12] = client;
    $[13] = recap;
    $[14] = refetchAuthorizationQuery;
    $[15] = submitTransactions;
    $[16] = targetChain;
    $[17] = toEnable;
    $[18] = toggleDialog;
    $[19] = transaction;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const execute = t3;
  let t4;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-title-sm">Review</span>;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== toggleDialog) {
    t5 = () => toggleDialog(false);
    $[22] = toggleDialog;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Close />;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t5) {
    t7 = <div className="flex items-center justify-between gap-[16px]">{t4}<Button variant="secondary" size="icon" aria-label="Close" onClick={t5}>{t6}</Button></div>;
    $[25] = t5;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== isEnableProbeUnavailable) {
    t8 = isEnableProbeUnavailable && <div className="gap-2xs flex flex-row items-center" role="alert"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">Couldn't verify the Auto-Deleverage contract. Please retry.</span></div>;
    $[27] = isEnableProbeUnavailable;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== account.isWalletTypeResolved || $[30] !== account.isWrongChain || $[31] !== client || $[32] !== execute || $[33] !== isEnableProbePending || $[34] !== isEnableProbeUnavailable || $[35] !== refetchAll || $[36] !== switchToTargetChain || $[37] !== toEnable || $[38] !== transaction) {
    t9 = account.isWrongChain ? <Button size="lg" onClick={switchToTargetChain}>Switch chain</Button> : isEnableProbeUnavailable ? <Button variant="secondary" size="lg" onClick={refetchAll}>Retry</Button> : <Button onClick={execute} size="lg" isLoading={isEnableProbePending} disabled={!transaction || !client || !account.isWalletTypeResolved}>{toEnable ? "Enable" : "Revoke"}</Button>;
    $[29] = account.isWalletTypeResolved;
    $[30] = account.isWrongChain;
    $[31] = client;
    $[32] = execute;
    $[33] = isEnableProbePending;
    $[34] = isEnableProbeUnavailable;
    $[35] = refetchAll;
    $[36] = switchToTargetChain;
    $[37] = toEnable;
    $[38] = transaction;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== recap || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t10 = <div className="gap-md flex flex-col" data-testid="confirm-dialog">{t7}{recap}{t8}{t9}</div>;
    $[40] = recap;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  return t10;
};
export default memo(AutoDeleverageReviewDialog);